import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";
import { useRouterCore } from "../../cores/router";
import {
  ContentGridRow,
  deserializeContentGrid,
} from "../utils/socialContentGridShare";

export default function PublicContentGrid() {
  const intl = useIntl();
  const { search } = useRouterCore();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const data = params.get("data");
  const [rows, setRows] = useState<ContentGridRow[]>(
    data ? deserializeContentGrid(data) : []
  );
  const [pendingDecision, setPendingDecision] = useState<{
    rowId: string;
    decision: "approved" | "rejected";
  } | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "cards">("grid");
  const [themeMode, setThemeMode] = useState<"light" | "dark">("light");
  const filteredRows = useMemo(() => {
    if (!monthFilter) return rows;
    return rows.filter((row) => row.date?.startsWith(monthFilter));
  }, [rows, monthFilter]);

  const updateComment = (id: string, comments: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id && !row.decisionLocked ? { ...row, comments } : row
      )
    );
  };

  const requestDecision = (id: string, decision: "approved" | "rejected") => {
    setDecisionError("");
    setPendingDecision({ rowId: id, decision });
  };

  const confirmDecision = () => {
    if (!pendingDecision) return;
    const targetRow = rows.find((row) => row.id === pendingDecision.rowId);

    if (pendingDecision.decision === "rejected" && !targetRow?.comments.trim()) {
      setDecisionError(
        intl.formatMessage({
          id: "app.socialContentGrid.rejectCommentRequired",
          defaultMessage: "Para rechazar, debes dejar un comentario obligatorio.",
        })
      );
      return;
    }

    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== pendingDecision.rowId || row.decisionLocked) return row;

        const isApproval = pendingDecision.decision === "approved";

        return {
          ...row,
          status: pendingDecision.decision,
          approvalCount: isApproval ? row.approvalCount + 1 : row.approvalCount,
          rejectionCount: isApproval ? row.rejectionCount : row.rejectionCount + 1,
          decisionLocked: true,
        };
      })
    );

    setDecisionError("");
    setPendingDecision(null);
  };

  const pendingDecisionTextId =
    pendingDecision?.decision === "approved"
      ? "app.socialContentGrid.confirmApproveDescription"
      : "app.socialContentGrid.confirmRejectDescription";

  const getStatusBadgeClass = (status: ContentGridRow["status"]) => {
    if (status === "approved") return "status-chip status-chip--approved";
    if (status === "rejected") return "status-chip status-chip--rejected";
    return "status-chip status-chip--pending";
  };

  const renderMediaPreview = (row: ContentGridRow) => {
    if (!row.mediaUrl) return "-";

    if (row.mediaType === "image") {
      return (
        <a href={row.mediaUrl} target="_blank" rel="noreferrer">
          <img
            src={row.mediaUrl}
            alt="public-media"
            style={{ maxWidth: 150, maxHeight: 140, objectFit: "cover" }}
            className="rounded"
          />
        </a>
      );
    }

    if (row.mediaType === "video") {
      return (
        <video
          src={row.mediaUrl}
          controls
          style={{ maxWidth: 180, maxHeight: 140 }}
          className="rounded"
        />
      );
    }

    if (row.mediaType === "link") {
      return (
        <a href={row.mediaUrl} target="_blank" rel="noreferrer">
          {intl.formatMessage({ id: "app.socialContentGrid.openLink" })}
        </a>
      );
    }

    return "-";
  };

  return (
    <div
      className={`p-4 social-content-grid-page social-content-grid-public-page social-content-grid-public-page--${themeMode}`}
    >
      <Container fluid>
        <Card className="border-0 shadow-sm mb-3 public-grid-hero-card">
          <CardBody className="d-flex flex-wrap align-items-center justify-content-between public-grid-hero">
            <div className="public-grid-hero-text">
              <h3 className="mb-1 public-grid-title">
                {intl.formatMessage({ id: "app.socialContentGrid.publicTitle" })}
              </h3>
              <p className="text-muted mb-0 public-grid-subtitle">
                {intl.formatMessage({ id: "app.socialContentGrid.publicDescription" })}
              </p>
            </div>
            <Badge color="light" pill className="px-3 py-2 border public-grid-counter">
              {filteredRows.length} / {rows.length} items
            </Badge>
          </CardBody>
        </Card>
        <Card className="border-0 shadow-sm mb-3">
          <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <label className="mb-0">
                {intl.formatMessage({
                  id: "app.socialContentGrid.filterByMonth",
                  defaultMessage: "Filtrar por mes",
                })}
              </label>
              <Input
                type="month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()}
                className="rounded"
              />
            </div>
            <div className="d-flex flex-wrap gap-2">
              <div className="public-theme-toggle d-flex align-items-center gap-1 px-2">
                <button
                  type="button"
                  className={`public-theme-toggle__btn ${
                    themeMode === "light" ? "public-theme-toggle__btn--active" : ""
                  }`}
                  onClick={() => setThemeMode("light")}
                  aria-label={intl.formatMessage({
                    id: "app.socialContentGrid.themeLight",
                    defaultMessage: "Light",
                  })}
                >
                  <FontAwesomeIcon icon={["fas", "sun"]} />
                </button>
                <button
                  type="button"
                  className={`public-theme-toggle__btn ${
                    themeMode === "dark" ? "public-theme-toggle__btn--active" : ""
                  }`}
                  onClick={() => setThemeMode("dark")}
                  aria-label={intl.formatMessage({
                    id: "app.socialContentGrid.themeDark",
                    defaultMessage: "Dark",
                  })}
                >
                  <FontAwesomeIcon icon={["fas", "moon"]} />
                </button>
              </div>
              <Button
                color={viewMode === "grid" ? "primary" : "secondary"}
                outline={viewMode !== "grid"}
                className="rounded-pill"
                onClick={() => setViewMode("grid")}
              >
                {intl.formatMessage({
                  id: "app.socialContentGrid.view.grid",
                  defaultMessage: "Grilla",
                })}
              </Button>
              <Button
                color={viewMode === "cards" ? "primary" : "secondary"}
                outline={viewMode !== "cards"}
                className="rounded-pill"
                onClick={() => setViewMode("cards")}
              >
                {intl.formatMessage({
                  id: "app.socialContentGrid.view.cards",
                  defaultMessage: "Cards",
                })}
              </Button>
              <Button
                color="secondary"
                outline
                className="rounded-pill"
                onClick={() => setMonthFilter("")}
                disabled={!monthFilter}
              >
                {intl.formatMessage({
                  id: "app.socialContentGrid.allMonths",
                  defaultMessage: "Todos los meses",
                })}
              </Button>
            </div>
          </CardBody>
        </Card>
        {viewMode === "grid" ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 public-grid-table">
                  <thead className="bg-light">
                    <tr>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.date" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.platform" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.format" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.media" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.copy" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.comments" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.status" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.history" })}</th>
                      <th>{intl.formatMessage({ id: "app.socialContentGrid.actions" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.date || "-"}</td>
                        <td className="text-capitalize">{row.platform || "-"}</td>
                        <td className="text-capitalize">{row.format || "-"}</td>
                        <td>{renderMediaPreview(row)}</td>
                        <td style={{ whiteSpace: "pre-wrap", minWidth: 220 }}>
                          {row.copy || "-"}
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <Input
                            type="textarea"
                            rows={2}
                            value={row.comments}
                            onChange={(e) => updateComment(row.id, e.target.value)}
                            placeholder={intl.formatMessage({
                              id: "app.socialContentGrid.commentPlaceholder",
                            })}
                            className="rounded"
                            disabled={row.decisionLocked}
                          />
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(row.status)}>
                            {intl.formatMessage({
                              id: `app.socialContentGrid.status.${row.status}`,
                            })}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-column public-grid-history">
                            <small className="public-grid-history-ok">A: {row.approvalCount}</small>
                            <small className="public-grid-history-ko">R: {row.rejectionCount}</small>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2 public-grid-actions">
                            <Button
                              color="success"
                              size="sm"
                              className="rounded-pill public-grid-action-btn"
                              onClick={() => requestDecision(row.id, "approved")}
                              disabled={row.decisionLocked}
                            >
                              {intl.formatMessage({ id: "app.socialContentGrid.approve" })}
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              className="rounded-pill public-grid-action-btn"
                              onClick={() => requestDecision(row.id, "rejected")}
                              disabled={row.decisionLocked}
                            >
                              {intl.formatMessage({ id: "app.socialContentGrid.reject" })}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        ) : (
          <div className="social-grid-cards social-grid-cards--cards">
            {filteredRows.map((row, index) => (
              <Card key={row.id} className="border-0 shadow-sm social-grid-item-card public-grid-item-card">
              <CardHeader className="social-grid-item-card__header d-flex justify-content-between align-items-center">
                <strong>{`#${index + 1}`}</strong>
                <span className={getStatusBadgeClass(row.status)}>
                  {intl.formatMessage({
                    id: `app.socialContentGrid.status.${row.status}`,
                  })}
                </span>
              </CardHeader>
              <CardBody>
                <div className="social-grid-item-card__body">
                  <div>
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.date" })}</label>
                    <div>{row.date || "-"}</div>
                  </div>
                  <div>
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.platform" })}</label>
                    <div className="text-capitalize">{row.platform || "-"}</div>
                  </div>
                  <div>
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.format" })}</label>
                    <div className="text-capitalize">{row.format || "-"}</div>
                  </div>
                  <div className="social-grid-card-field--wide">
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.media" })}</label>
                    <div>{renderMediaPreview(row)}</div>
                  </div>
                  <div className="social-grid-card-field--wide">
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.copy" })}</label>
                    <div style={{ whiteSpace: "pre-wrap" }}>{row.copy || "-"}</div>
                  </div>
                  <div className="social-grid-card-field--wide">
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.comments" })}</label>
                    <Input
                      type="textarea"
                      rows={3}
                      value={row.comments}
                      onChange={(e) => updateComment(row.id, e.target.value)}
                      placeholder={intl.formatMessage({
                        id: "app.socialContentGrid.commentPlaceholder",
                      })}
                      className="rounded"
                      disabled={row.decisionLocked}
                    />
                  </div>
                  <div>
                    <label>{intl.formatMessage({ id: "app.socialContentGrid.history" })}</label>
                    <div className="d-flex flex-column public-grid-history">
                      <small className="public-grid-history-ok">A: {row.approvalCount}</small>
                      <small className="public-grid-history-ko">R: {row.rejectionCount}</small>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 public-grid-actions mt-3">
                  <Button
                    color="success"
                    size="sm"
                    className="rounded-pill public-grid-action-btn"
                    onClick={() => requestDecision(row.id, "approved")}
                    disabled={row.decisionLocked}
                  >
                    {intl.formatMessage({ id: "app.socialContentGrid.approve" })}
                  </Button>
                  <Button
                    color="danger"
                    size="sm"
                    className="rounded-pill public-grid-action-btn"
                    onClick={() => requestDecision(row.id, "rejected")}
                    disabled={row.decisionLocked}
                  >
                    {intl.formatMessage({ id: "app.socialContentGrid.reject" })}
                  </Button>
                </div>
              </CardBody>
            </Card>
            ))}
          </div>
        )}
        {filteredRows.length === 0 && (
          <Card className="border-0 shadow-sm mt-3">
            <CardBody className="text-center text-muted py-4">
              {intl.formatMessage({
                id: monthFilter
                  ? "app.socialContentGrid.emptyFiltered"
                  : "app.socialContentGrid.emptyPublicGrid",
                defaultMessage: monthFilter
                  ? "No hay contenidos para el mes seleccionado."
                  : undefined,
              })}
            </CardBody>
          </Card>
        )}
        <Modal isOpen={Boolean(pendingDecision)} toggle={() => setPendingDecision(null)}>
          <ModalHeader toggle={() => setPendingDecision(null)}>
            {intl.formatMessage({ id: "app.socialContentGrid.confirmDecisionTitle" })}
          </ModalHeader>
          <ModalBody>
            {intl.formatMessage({ id: pendingDecisionTextId })}
            {pendingDecision?.decision === "rejected" && (
              <p className="mt-3 mb-0 text-danger font-weight-bold">
                {intl.formatMessage({
                  id: "app.socialContentGrid.rejectCommentHint",
                  defaultMessage:
                    "Debes completar el comentario de esta fila antes de confirmar el rechazo.",
                })}
              </p>
            )}
            {decisionError && (
              <p className="mt-2 mb-0 text-danger font-weight-bold">{decisionError}</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setDecisionError("");
                setPendingDecision(null);
              }}
            >
              {intl.formatMessage({ id: "app.socialContentGrid.cancel" })}
            </Button>
            <Button
              color={pendingDecision?.decision === "approved" ? "success" : "danger"}
              onClick={confirmDecision}
            >
              {intl.formatMessage({ id: "app.socialContentGrid.confirmAndSend" })}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
